/**
 * @fileoverview Tracking problem dialog component for handling order tracking issues.
 * Provides functionality to manage and resolve tracking-related problems for orders
 * with specific tags requiring attention.
 *
 * @author Web Analysis Team
 * @version 1.0.0
 */

import React, { useEffect, useState } from 'react';
import { toLocalTime } from '../../../../../components/ex_dayjs';
import { Button, Grid, IconButton, Link } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import MDialog from '../../../../../components/controls/MDialog';
import {
    Create, TextInput, required,
    SelectInput,
    SaveButton,
    SimpleForm,
    useNotify,
} from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { IData } from '../../../../../types';

/**
 * Tracking configuration name constant for problem tracking workflows.
 * @type {string}
 * @constant
 */
const TRACKING_NAME = 'ProblemTracking';

/**
 * Tags that trigger the tracking problem dialog display.
 * @type {Array<string>}
 * @constant
 */
const tagsTracking = ['Fix'];

/**
 * Determines if tracking problem configuration should be shown for an order.
 *
 * @param {Object} order - The order object to check
 * @param {string} tags - Comma-separated list of order tags
 * @returns {Object|null} Configuration object if tracking is required, null otherwise
 */
export const onConfigTrackingProblem = (order, tags) => {
    let isShow = tagsTracking.some(x => tags.includes(x));
    let orderProblem;
    if (order.ordersProcessTracking && !order.orderProblemTracking) {
        orderProblem = order.ordersProcessTracking.find((x: any) => x.category === TRACKING_NAME);
    }
    return isShow || !!orderProblem;
};

/** Props for {@link FormContent}. */
interface FormContentProps {
    /** Dialog data: the tracking record plus the list of known process-tracking errors. */
    data: IData;
    /** Called to close the dialog. */
    handleClose: (...args: any[]) => void;
}

const FormContent: React.FC<FormContentProps> = ({ data, handleClose }) => {
    const { record, processTrackingErrs } = data;
    const { setValue } = useFormContext();

    const [dictionary, setDictionaries] = useState({
        listErrors: [],
        listActions: []
    });

    useEffect(() => {
        let issue_category = data.error;
        if (issue_category) {
            let tmp = issue_category.split(':');
            data.issue_category = tmp[0];
            if (tmp[1]) setValue('description', tmp[1].trim());
        }
        setDictionaries((prev) => ({
            ...prev,
            listErrors: processTrackingErrs.map((x: any) => x.trim()),
            listActions: ['Fixing', 'Fixed']
        }) as any);
    }, [record]);

    return (
        <div style={{ width: '100%' }}>
            <Grid size={12} style={{ marginTop: -20, marginBottom: '10px' }}>
                <span><b>Author:&nbsp;</b>{record.author}&nbsp;|&nbsp;<b>Tracking Time:&nbsp;</b>{record.tracking_time}</span>
            </Grid>
            {/* Row 1 */}
            <Grid container size={12} spacing={2}>
                <Grid size={6}>
                    <SelectInput source="issue_category" validate={required()} choices={dictionary.listErrors} translateChoice={false} resettable variant="outlined" />
                </Grid>
                <Grid size={6}>
                    <SelectInput source="action" choices={dictionary.listActions} translateChoice={false} resettable variant="outlined" />
                </Grid>
            </Grid>
            {/* Row 2 */}
            <Grid size={12}>
                <TextInput source="description" placeholder="Input description for issue category" value={record.description} multiline minRows={2} resettable variant="outlined" />
            </Grid>
            <Grid container justifyContent={'flex-end'} spacing={1} style={{ marginBottom: -30 }}>
                <Grid><SaveButton variant="contained" color="primary" label={'Save Tracking'} /></Grid>
                <Grid><Button onClick={handleClose} variant='outlined' aria-label={'h_isOpen'}>Close</Button></Grid>
            </Grid>
        </div>
    );
};

/** Props for {@link TrackingProblemPopup}. */
interface TrackingProblemProps {
    /** Vendor configuration object. */
    vendor: any;
    /** Data provider for API operations. */
    dataProvider: any;
    /** Data containing order and dialog state information. */
    data: IData;
    /** Fallback callback invoked with the saved problem tracking record when `data.dlgState.callback` is not provided. */
    callback?: (...args: any[]) => void;
}

const TrackingProblemPopup: React.FC<TrackingProblemProps> = ({ vendor, dataProvider, data, callback }) => {
    const { order } = data;
    const [isOpen, setOpen] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [trackingItem, setTrackingItem] = useState<IData>({});
    const [listError, setListError] = useState<any[]>([]);
    const saveCallback = data.dlgState.callback || callback;
    const notify = useNotify();

    useEffect(() => {
        if (isOpen) {
            let trackingErrors = (order.orderProcessTrackingErrors || []).filter((x: any) => x.category === TRACKING_NAME).map((x: any) => x.error);
            setListError(trackingErrors);
            let record: IData = {
                order_id: order.id,
                order_number: order.order_number
            };
            const problemData = order.ordersProcessTracking?.find((x: any) => x.category === TRACKING_NAME);
            if (problemData) {
                let _error = problemData.error;
                let issue_category, description;
                if (_error) {
                    let tmp = _error.split(':');
                    issue_category = tmp[0];
                    if (tmp[1]) description = tmp[1].trim();
                }
                record = {
                    ...record,
                    id: problemData.id,
                    author: problemData.author,
                    tracking_time: problemData.utc_Posted_On,
                    issue_category,
                    description,
                    action: problemData.action,
                    category: TRACKING_NAME
                };
                record.tracking_time = record.tracking_time ? toLocalTime(record.tracking_time).format('MMM D YYYY hh:mm A') : '';
            }
            setTrackingItem(record);
        }

    }, [isOpen]);

    const onSubmit = (data) => {
        let problemData = {
            id: data.id,
            order_id: data.order_id,
            order_number: data.order_number,
            action: data.action,
            error: data.description ? data.issue_category + ': ' + data.description : data.issue_category,
            category: TRACKING_NAME
        };
        setLoading(true);
        dataProvider.postData(vendor.controller, 'SaveProblemTracking', problemData)
            .then((response: any) => {
                setLoading(false);
                if (response && response.data.success) {
                    onClose();
                    notify('Saved Tracking successfully!', { type: 'success', autoHideDuration: 1000 });
                    saveCallback(response.data.orderProblem);
                }
            });
    };

    const onClose = () => setOpen(false);

    return (
        <>
            {
                data.dlgState.btn_problemTracking &&
                <>
                    <IconButton title="Problem Tracking" size="small"
                        onClick={() => setOpen(true)}
                        sx={{
                            borderRadius: '4px',
                            background: '#d9534f', '&:hover': {
                                backgroundColor: 'red',
                            },
                            marginLeft: '3px'
                        }}><ErrorIcon />
                    </IconButton>
                    <Create title={' '} record={trackingItem}>
                        <MDialog
                            title={
                                <>
                                    PROBLEM ORDER TRACKING #
                                    <Link
                                        href={`${location.origin}/ProblemOrderTrackingReport?Order_Number_eq=${trackingItem.order_number}`}
                                        target="_blank"
                                        underline="hover"
                                        sx={{
                                            fontSize: 'inherit !important',
                                            fontWeight: 'inherit',
                                            lineHeight: 'inherit',
                                            verticalAlign: 'baseline'
                                        }}
                                    >
                                        {trackingItem.order_number}
                                    </Link>
                                </>
                            }
                            maxWidth={'md'}
                            open={isOpen}
                            handleClose={onClose}
                            name={'prolemTrk_isOpen'}
                            footer={false}
                            disabled={isLoading}
                            msgLoading={'Saving...'}
                            className={'order-dialog'}
                        >
                            <SimpleForm defaultValues={order.orderProcessTracking || {}} toolbar={false} mode="onBlur" reValidateMode="onBlur" onSubmit={onSubmit}>
                                <FormContent
                                    handleClose={onClose}
                                    data={{
                                        record: trackingItem,
                                        processTrackingDics: order.orderProcessTrackingDictionaries || {},
                                        processTrackingErrs: listError
                                    }}
                                />
                            </SimpleForm>
                        </MDialog>
                    </Create>
                </>
            }
        </>
    );
};
export default TrackingProblemPopup;
